import React, { useState } from 'react';
import { getCareerAdvice } from '../services/gemini';
import { EngineerLevel, RoleTrack } from '../types';

const AICareerCoach: React.FC = () => {
  const [skills, setSkills] = useState('');
  const [track, setTrack] = useState(RoleTrack.OPS);
  const [level, setLevel] = useState(EngineerLevel.CLOUD_ENGINEER);
  const [struggles, setStruggles] = useState('');
  const [loading, setLoading] = useState(false);
  const [advice, setAdvice] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await getCareerAdvice(skills, track, level, struggles);
      setAdvice(result || "Unable to generate advice at this time.");
    } catch (err: any) {
      console.error(err);
      setAdvice("Error connecting to the architect's brain. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="career-coach" className="py-20 px-6 max-w-5xl mx-auto">
      <div className="glass p-10 rounded-3xl border-orange-500/20 shadow-2xl shadow-orange-500/5">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl">
            🤖
          </div>
          <div>
            <h2 className="text-3xl font-bold">AI Progression Architect</h2>
            <p className="text-slate-400">Get a blunt, technical roadmap to the next level.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Current Skills</label>
              <input 
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
                placeholder="e.g. EC2, S3, some Terraform..."
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500 outline-none transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Target Role Track</label>
              <select 
                value={track}
                onChange={(e) => setTrack(e.target.value as RoleTrack)}
                className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500 outline-none transition-all"
              >
                {Object.values(RoleTrack).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Target Level</label>
            <select 
              value={level}
              onChange={(e) => setLevel(e.target.value as EngineerLevel)}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500 outline-none transition-all"
            >
              {Object.values(EngineerLevel).map((lv) => (
                <option key={lv} value={lv}>{lv}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Your Main Struggle</label>
            <textarea 
              value={struggles}
              onChange={(e) => setStruggles(e.target.value)}
              placeholder="e.g. I spend 8 hours a day closing manual Jira tickets and have no time for architecture."
              rows={3}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500 outline-none transition-all"
              required
            />
          </div>

          <button 
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-700 rounded-xl font-bold text-lg transition-all shadow-lg shadow-orange-500/20 flex items-center justify-center gap-3"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Synthesizing Roadmap...
              </>
            ) : 'Get My Roadmap'}
          </button>
        </form>

        {advice && (
          <div className="mt-12 p-8 bg-slate-950/80 border border-orange-500/30 rounded-2xl animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <span className="text-orange-500 text-2xl">⚡</span> Architect's Verdict
            </h3>
            <div className="prose prose-invert max-w-none text-slate-300 whitespace-pre-line leading-relaxed font-medium">
              {advice}
            </div>
            <div className="mt-8 pt-6 border-t border-slate-800 flex justify-between items-center text-xs text-slate-500">
              <span>Powered by AWS Architect AI</span>
              <button onClick={() => setAdvice(null)} className="hover:text-orange-400 transition-colors uppercase font-bold tracking-widest">Clear</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AICareerCoach;